/**
 * Battery presets — UK-available commercial / small-C&I LFP systems.
 *
 * Each entry supplies the 8 BatteryParams fields used by the DP plus
 * display-only metadata (rated capacity, chemistry, approximate hardware
 * price range in GBP).
 *
 * Prices are HARDWARE ONLY, indicative 2026 UK reseller bands — they
 * exclude installation, commissioning, DNO G99 fees, and VAT. Treat them
 * as "roughly" figures for simple payback comparison, not quotes.
 *
 * Spec-sheet values are sourced from manufacturer datasheets. If a stack
 * is described (e.g. "×3"), the power figures are the parallel-combined
 * inverter rating.
 */

export const BATTERY_PRESETS = [
  {
    id: 'sungrow-sh15t-sbh400',
    name: 'Sungrow SH15T + SBH400',
    subtitle: '40 kWh LFP, 3-phase hybrid inverter',
    rated_kwh: 40,
    chemistry: 'LFP',
    price_gbp_min: 13000,
    price_gbp_max: 16000,
    notes: 'DC-coupled hybrid. Max 8 modules per stack (SBH400). Solar-ready.',
    params: {
      capacity_kwh_usable: 36,       // 40 × 0.90 (5-95% SoC band)
      max_charge_kw: 27,
      max_discharge_kw: 15,
      eta_charge: 0.96,
      eta_discharge: 0.96,
      grid_import_cap_kw: 22.08,     // UK 32A × 3ph × 230V
      initial_soc_kwh: 18,
      standby_kw: 0.02,
    },
  },
  {
    id: 'foxess-h3-ecs',
    name: 'Fox ESS H3-25 + ECS4800-H5',
    subtitle: '24 kWh LFP, 3-phase hybrid inverter',
    rated_kwh: 24,
    chemistry: 'LFP',
    price_gbp_min: 8000,
    price_gbp_max: 10500,
    notes: '5× ECS4800 modules stacked. Symmetric 25 kW charge/discharge.',
    params: {
      capacity_kwh_usable: 21.6,     // 24 × 0.90
      max_charge_kw: 25,
      max_discharge_kw: 25,
      eta_charge: 0.95,
      eta_discharge: 0.95,
      grid_import_cap_kw: 22.08,
      initial_soc_kwh: 10.8,
      standby_kw: 0.025,
    },
  },
  {
    id: 'huawei-luna2000-30',
    name: 'Huawei LUNA2000-30 + SUN2000-15KTL',
    subtitle: '30 kWh LFP, 3-phase hybrid inverter',
    rated_kwh: 30,
    chemistry: 'LFP',
    price_gbp_min: 10000,
    price_gbp_max: 13000,
    notes: 'Stackable 5 kWh modules. 15 kW inverter caps power in/out.',
    params: {
      capacity_kwh_usable: 27,       // 30 × 0.90
      max_charge_kw: 15,
      max_discharge_kw: 15,
      eta_charge: 0.96,
      eta_discharge: 0.96,
      grid_import_cap_kw: 22.08,
      initial_soc_kwh: 13.5,
      standby_kw: 0.02,
    },
  },
  {
    id: 'givenergy-aio-x3',
    name: 'GivEnergy All-in-One ×3',
    subtitle: '40.5 kWh LFP, 3-phase via 3× single-phase units',
    rated_kwh: 40.5,
    chemistry: 'LFP',
    price_gbp_min: 17000,
    price_gbp_max: 20000,
    notes: 'UK-manufactured. Integrated inverter per unit (6 kW × 3 = 18 kW). Lower RTE than commercial-grade.',
    params: {
      capacity_kwh_usable: 38.4,     // 40.5 × 0.95 (slightly wider usable band per spec)
      max_charge_kw: 18,
      max_discharge_kw: 18,
      eta_charge: 0.94,
      eta_discharge: 0.94,
      grid_import_cap_kw: 22.08,
      initial_soc_kwh: 19,
      standby_kw: 0.04,
    },
  },
  {
    id: 'pylontech-force-h2-solis',
    name: 'Pylontech Force H2 + Solis 3P-30K',
    subtitle: '35.5 kWh LFP, 3-phase hybrid inverter',
    rated_kwh: 35.5,
    chemistry: 'LFP',
    price_gbp_min: 9000,
    price_gbp_max: 12000,
    notes: '10× 3.55 kWh modules. Cheap kWh/£, Solis hybrid handles power conversion.',
    params: {
      capacity_kwh_usable: 32,       // 35.5 × 0.90
      max_charge_kw: 30,
      max_discharge_kw: 30,
      eta_charge: 0.95,
      eta_discharge: 0.95,
      grid_import_cap_kw: 22.08,
      initial_soc_kwh: 16,
      standby_kw: 0.03,
    },
  },
  {
    id: 'tesla-pw3-single',
    name: 'Tesla Powerwall 3 (single)',
    subtitle: '13.5 kWh LFP, integrated inverter',
    rated_kwh: 13.5,
    chemistry: 'LFP',
    price_gbp_min: 8000,
    price_gbp_max: 10000,
    notes: 'Single unit with integrated 11.5 kW inverter. Floor/wall mount. Gateway 3 required for grid-tie dispatch (adds ~£500).',
    params: {
      capacity_kwh_usable: 13.4,      // ~99% usable per Tesla spec
      max_charge_kw: 11.5,
      max_discharge_kw: 11.5,
      eta_charge: 0.95,
      eta_discharge: 0.95,
      grid_import_cap_kw: 22.08,
      initial_soc_kwh: 6.7,
      standby_kw: 0.012,
    },
  },
  {
    id: 'tesla-pw3-x3-gateway',
    name: 'Tesla Powerwall 3 ×3 + Gateway 3',
    subtitle: '40.5 kWh LFP, integrated dispatch via Gateway 3',
    rated_kwh: 40.5,
    chemistry: 'LFP',
    price_gbp_min: 24000,
    price_gbp_max: 28000,
    notes: 'Includes Gateway 3 for whole-home backup + Tesla app dispatch (commercial-grade Autobidder is Megapack-only; consumer Powerwall gets storm-watch + VPP participation).',
    params: {
      capacity_kwh_usable: 40,       // ~99% usable per Tesla spec (conservative on cycle life)
      max_charge_kw: 33,             // 11.5 kW × 3 continuous
      max_discharge_kw: 33,
      eta_charge: 0.95,
      eta_discharge: 0.95,
      grid_import_cap_kw: 22.08,
      initial_soc_kwh: 20,
      standby_kw: 0.035,
    },
  },
]

/** Preset entry used when the user has manually tweaked params off of a preset. */
export const CUSTOM_PRESET_ID = 'custom'

/**
 * Find the preset whose params match the supplied BatteryParams exactly.
 * Returns the preset id, or CUSTOM_PRESET_ID if no match.
 * Uses a 1e-6 tolerance to survive float round-trips.
 */
export function identifyPreset(battery) {
  if (!battery) return CUSTOM_PRESET_ID
  const keys = Object.keys(BATTERY_PRESETS[0].params)
  for (const preset of BATTERY_PRESETS) {
    const match = keys.every(k => Math.abs((battery[k] ?? NaN) - preset.params[k]) < 1e-6)
    if (match) return preset.id
  }
  return CUSTOM_PRESET_ID
}

/** Midpoint of the preset's price range, used for simple-payback display */
export function presetMidPrice(preset) {
  if (!preset) return null
  return (preset.price_gbp_min + preset.price_gbp_max) / 2
}

/** Look up a preset by id; returns undefined for 'custom' */
export function getPreset(id) {
  return BATTERY_PRESETS.find(p => p.id === id)
}

/**
 * Preset with the lowest £/kWh (midpoint price ÷ rated kWh). Used as
 * the default when the user first enables a battery combo and as the
 * label fallback on charts/payback tiles when no battery has been
 * simulated yet.
 */
export function getCheapestBatteryPreset() {
  return BATTERY_PRESETS.reduce((best, p) => {
    const pricePerKwh = ((p.price_gbp_min + p.price_gbp_max) / 2) / p.rated_kwh
    const bestPricePerKwh = ((best.price_gbp_min + best.price_gbp_max) / 2) / best.rated_kwh
    return pricePerKwh < bestPricePerKwh ? p : best
  }, BATTERY_PRESETS[0])
}
